import React, { createContext, useEffect, useMemo, useState } from "react";
import { CssBaseline } from "@material-ui/core";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import {
  applyFluyraTheme,
  buildFluyraMuiTheme,
  FLUYRA_ALLOWED_THEMES,
  FLUYRA_DEFAULT_THEME,
  getStoredFluyraTheme,
  markFluyraThemeAsExplicit,
} from "./fluyraTheme";

export const ThemeContext = createContext({
  theme: FLUYRA_DEFAULT_THEME,
  setTheme: () => {},
  toggleTheme: () => {},
  colorMode: {
    theme: FLUYRA_DEFAULT_THEME,
    toggleColorMode: () => {},
    setTheme: () => {},
    setPrimaryColorLight: () => {},
    setPrimaryColorDark: () => {},
    setAppLogoLight: () => {},
    setAppLogoDark: () => {},
    setSidebarLogoLight: () => {},
    setSidebarLogoDark: () => {},
    setAppLogoFavicon: () => {},
    setAppName: () => {},
  },
});

export const FluyraThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => getStoredFluyraTheme());

  useEffect(() => {
    applyFluyraTheme(theme);
  }, [theme]);

  const setTheme = (nextTheme) => {
    markFluyraThemeAsExplicit();
    setThemeState(
      FLUYRA_ALLOWED_THEMES.includes(nextTheme)
        ? nextTheme
        : FLUYRA_DEFAULT_THEME
    );
  };

  const toggleTheme = () => {
    markFluyraThemeAsExplicit();
    setThemeState((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  };

  const muiTheme = useMemo(() => buildFluyraMuiTheme(theme), [theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      colorMode: {
        theme,
        toggleColorMode: toggleTheme,
        setTheme,
        setPrimaryColorLight: () => {},
        setPrimaryColorDark: () => {},
        setAppLogoLight: () => {},
        setAppLogoDark: () => {},
        setSidebarLogoLight: () => {},
        setSidebarLogoDark: () => {},
        setAppLogoFavicon: () => {},
        setAppName: () => {},
      },
    }),
    [theme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
